import { Injectable } from '@angular/core';
import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';

import { apiPaths } from '../constants/api.const';
import { ApiLogService } from '../../../shared/services/api-log.service';

@Injectable()
export class ApiLoggingInterceptor implements HttpInterceptor {
  constructor(private apiLogService: ApiLogService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const isCalculationRequest = this.isCalculationUrl(req.urlWithParams);
    const entry = this.apiLogService.createEntry({
      timestamp: new Date().toISOString(),
      method: req.method,
      url: req.urlWithParams,
      payload: req.body ?? null,
      response: null,
      code: null
    });

    return next.handle(req).pipe(
      tap(event => {
        if (event instanceof HttpResponse) {
          let responseBody = this.normalizeResponseBody(event.body);
          if (isCalculationRequest && responseBody !== null && !this.hasCalculationError(responseBody)) {
            responseBody = 'Calculation response';
          }
          this.apiLogService.updateEntry(entry.id, {
            response: responseBody,
            code: event.status
          });
        }
      }),
      catchError((error: HttpErrorResponse) => {
        const fallbackMessage = error?.message || null;
        const isProgressEvent = typeof ProgressEvent !== 'undefined' && error?.error instanceof ProgressEvent;
        const responsePayload = (error?.error === null || error?.error === undefined || error?.error === '' || isProgressEvent)
          ? fallbackMessage
          : error?.error;
        this.apiLogService.updateEntry(entry.id, {
          response: responsePayload,
          code: typeof error?.status === 'number' ? error.status : null
        });
        return throwError(() => error);
      })
    );
  }

  private isCalculationUrl(url: string): boolean {
    const markers = [
      apiPaths.study.calculate,
      apiPaths.study.pullSampleData,
      apiPaths.study.getCalculateCsv,
      apiPaths.study.pollCalculationStatus
    ];
    return markers.some(marker => url.includes(marker));
  }

  private normalizeResponseBody(body: unknown): unknown | null {
    if (body == null) {
      return null;
    }
    if (body instanceof Blob) {
      return {
        type: 'blob',
        size: body.size,
        mime: body.type
      };
    }
    return body;
  }

  private hasCalculationError(body: unknown): boolean {
    if (!body || typeof body !== 'object') {
      return false;
    }
    const record = body as Record<string, unknown>;
    const status = typeof record['status'] === 'string' ? record['status'].toLowerCase() : '';
    const error = typeof record['error'] === 'string' ? record['error'].toLowerCase() : '';
    return status === 'error' || status === 'fail' || status === 'failed' || error === 'error';
  }
}
